<template>
  <FilterChipsList
    @deleteFilter="$emit('deleteFilter', $event)"
    ref="filterChipsRef"
    :chipsConfig="chipsConfig"
  />
</template>

<script setup>
import { useUserStore } from "@/stores/user";
import { useUnitStore } from "@/stores/unit";
import { useOrganizationStore } from "@/stores/organization";
import { useTeamStore } from "@/stores/team";
import { BASE_TIMEZONE } from "@/utils/constants";

import { formatInTimeZone } from "@/utils/dates";
import { getUserNameAndFallbacksFromRawData } from "@/utils/utils";

const filterChipsRef = ref();

const route = useRoute();
const userStore = useUserStore();
const unitsStore = useUnitStore();
const organizationStore = useOrganizationStore();
const teamsStore = useTeamStore();

const chipsConfig = ref();

watch(
  () => route.query,
  async () => {
    chipsConfig.value = {
      access_channel_status_text: {
        value: [route.query.access_channel_status_text] ?? [],
        display: (value) =>
          value.map((v) => capitalizeFirstLetter(v)).join(", "),
      },
      user_full_name: {
        value: route.query.user_full_name ?? "",
        display: (value) =>
          getUserNameAndFallbacksFromRawData(
            userStore.allDict[value]
          ),
      },
      assignee: {
        value: (route.query.assignee
          ? Array.isArray(route.query.assignee)
            ? route.query.assignee
            : [route.query.assignee]
          : []
        ).filter((id) => id !== userStore.authUserId),
        display: (value) => {
          if (!Array.isArray(value)) {
            value = [value];
          }
          1;

          return value
            .map((id) => {
              return getUserNameAndFallbacksFromRawData(
                userStore.allDict[id]
              );
            })
            .join(", ");
        },
      },
      dt_open_start_date: {
        value: route.query.dt_open_start_date
          ? parseInt(route.query.dt_open_start_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      dt_open_end_date: {
        value: route.query.dt_open_end_date
          ? parseInt(route.query.dt_open_end_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      created_start_date: {
        value: route.query.created_start_date
          ? parseInt(route.query.created_start_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      created_end_date: {
        value: route.query.created_end_date
          ? parseInt(route.query.created_end_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      last_modified_start_date: {
        value: route.query.last_modified_start_date
          ? parseInt(route.query.last_modified_start_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      last_modified_end_date: {
        value: route.query.last_modified_end_date
          ? parseInt(route.query.last_modified_end_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      dt_closed_start_date: {
        value: route.query.dt_closed_start_date
          ? parseInt(route.query.dt_closed_start_date)
          : null,
        display: (value) => value,
      },
      dt_closed_end_date: {
        value: route.query.dt_closed_end_date
          ? parseInt(route.query.dt_closed_end_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      dt_user_responded_start_date: {
        value: route.query.dt_user_responded_start_date
          ? parseInt(route.query.dt_user_responded_start_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      dt_user_responded_end_date: {
        value: route.query.dt_user_responded_end_date
          ? parseInt(route.query.dt_user_responded_end_date)
          : null,
        display: (value) =>
          formatInTimeZone(value, BASE_TIMEZONE, "DD MMM YYYY"),
      },
      user_id: {
        value:
          route.query.user_id && route.query.user_id !== userStore.authUserId
            ? route.query.user_id
            : null,
        display: (value) =>
          getUserNameAndFallbacksFromRawData(
            userStore.allDict[value]
          ),
      },
      company_id: {
        value: route.query.company_id ?? null,
        display: (value) => organizationStore.allDict[value]?.name,
      },
      last_modify_by: {
        value: route.query.last_modify_by ?? null,
        display: (value) =>
          getUserNameAndFallbacksFromRawData(
            userStore.allDict[value]
          ),
      },
      team: {
        value: route.query.team ?? [],
        display: (value) => {
          teamsStore.allDict[value]?.name;

          if (!Array.isArray(value)) {
            value = [value];
          }

          return value
            .map((id) => {
              return teamsStore.allDict[id]?.name;
            })
            .join(", ");
        },
      },
      unit_name: {
        value: route.query.unit_name ?? null,
        display: (value) => unitsStore.allDict[value]?.store_name,
      },
    };
  },
  {
    deep: true,
    immediate: true,
  }
);

function capitalizeFirstLetter(val) {
  if (val && typeof val === "string") {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
  }
  return val;
}

const hasAnyFilterApplied = computed(() => {
  return filterChipsRef.value?.chips.length;
});

defineExpose({
  chipsConfig,
  hasAnyFilterApplied,
});
</script>
